var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RadixButton } from "../primitives/RadixButton";
import { RadixFlyOut } from "../primitives/RadixFlyOut";
import { RadixInput } from "../primitives/RadixInput";
import { RadixComboBox } from "../primitives/RadixComboBox";
import { StateCodeComboBox } from "../primitives/StateCodeComboBox";
import { editAddressFlyOutSchema } from "../editAddressFlyOut";
import "./addressFlyOut.css";
import { SaveIcon } from "../icons";
import { toUspsStateCode } from "../utility/uspsStates";
var getMapboxAccessToken = function (explicitToken) {
    if (explicitToken && explicitToken.trim()) {
        return explicitToken.trim();
    }
    var session = window.session;
    var sessionToken = String((session === null || session === void 0 ? void 0 : session.mapbox_access_token) || (session === null || session === void 0 ? void 0 : session.mapboxAccessToken) || (session === null || session === void 0 ? void 0 : session.map_box_access_token) || "").trim();
    return sessionToken;
};
var toSuggestion = function (feature) {
    var context = Array.isArray(feature === null || feature === void 0 ? void 0 : feature.context) ? feature.context : [];
    var getContextText = function (idPrefix) {
        var match = context.find(function (entry) { return String((entry === null || entry === void 0 ? void 0 : entry.id) || "").startsWith(idPrefix); });
        return String((match === null || match === void 0 ? void 0 : match.text) || "").trim();
    };
    var regionEntry = context.find(function (entry) { return String((entry === null || entry === void 0 ? void 0 : entry.id) || "").startsWith("region"); });
    var shortCode = String((regionEntry === null || regionEntry === void 0 ? void 0 : regionEntry.short_code) || "").trim();
    var resolvedStateValue = shortCode.includes("-") ? shortCode.split("-")[1] : String((regionEntry === null || regionEntry === void 0 ? void 0 : regionEntry.text) || "").trim();
    var state = toUspsStateCode(resolvedStateValue);
    var number = String((feature === null || feature === void 0 ? void 0 : feature.address) || "").trim();
    var street = String((feature === null || feature === void 0 ? void 0 : feature.text) || "").trim();
    return {
        id: String((feature === null || feature === void 0 ? void 0 : feature.id) || "".concat((feature === null || feature === void 0 ? void 0 : feature.place_name) || street)),
        label: String((feature === null || feature === void 0 ? void 0 : feature.place_name) || "").trim(),
        address: [number, street].filter(Boolean).join(" "),
        city: getContextText("place"),
        state: state,
        zip: getContextText("postcode"),
    };
};
export var AddressFlyOut = function (_a) {
    var open = _a.open, onOpenChange = _a.onOpenChange, onSubmit = _a.onSubmit, defaultValues = _a.defaultValues, title = _a.title, description = _a.description, submitLabel = _a.submitLabel, _b = _a.isSubmitting, isSubmitting = _b === void 0 ? false : _b, mapboxAccessToken = _a.mapboxAccessToken;
    var _c = useForm({
        resolver: zodResolver(editAddressFlyOutSchema),
        mode: "onBlur",
        defaultValues: defaultValues,
    }), control = _c.control, handleSubmit = _c.handleSubmit, reset = _c.reset, setValue = _c.setValue, watch = _c.watch, _d = _c.formState, errors = _d.errors, isValid = _d.isValid;
    var _e = React.useState([]), suggestions = _e[0], setSuggestions = _e[1];
    var _f = React.useState(false), isFetchingSuggestions = _f[0], setIsFetchingSuggestions = _f[1];
    var _g = React.useState(null), activeSuggestionId = _g[0], setActiveSuggestionId = _g[1];
    var _h = React.useState(""), selectedAddressQuery = _h[0], setSelectedAddressQuery = _h[1];
    var _j = React.useState(false), isAddressInputFocusedByClick = _j[0], setIsAddressInputFocusedByClick = _j[1];
    var token = React.useMemo(function () { return getMapboxAccessToken(mapboxAccessToken); }, [mapboxAccessToken]);
    var addressValue = watch("address") || "";
    React.useEffect(function () {
        if (open) {
            reset(defaultValues);
            setSuggestions([]);
            setActiveSuggestionId(null);
            setSelectedAddressQuery("");
            setIsAddressInputFocusedByClick(false);
        }
    }, [defaultValues, open, reset]);
    React.useEffect(function () {
        if (!open || !token || !isAddressInputFocusedByClick) {
            setSuggestions([]);
            setIsFetchingSuggestions(false);
            return;
        }
        var query = String(addressValue || "").trim();
        if (query === selectedAddressQuery) {
            setSuggestions([]);
            setIsFetchingSuggestions(false);
            return;
        }
        if (query.length < 4) {
            setSuggestions([]);
            setIsFetchingSuggestions(false);
            return;
        }
        var abortController = new AbortController();
        var timeoutId = window.setTimeout(function () { return __awaiter(void 0, void 0, void 0, function () {
            var params, response, payload, features, _error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setIsFetchingSuggestions(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, 5, 6]);
                        params = new URLSearchParams({
                            access_token: token,
                            autocomplete: "true",
                            country: "US",
                            limit: "5",
                            types: "address",
                        });
                        return [4 /*yield*/, fetch("https://api.mapbox.com/geocoding/v5/mapbox.places/".concat(encodeURIComponent(query), ".json?").concat(params.toString()), { signal: abortController.signal })];
                    case 2:
                        response = _a.sent();
                        if (!response.ok) {
                            throw new Error("Mapbox autocomplete failed: ".concat(response.status));
                        }
                        return [4 /*yield*/, response.json()];
                    case 3:
                        payload = _a.sent();
                        features = Array.isArray(payload === null || payload === void 0 ? void 0 : payload.features) ? payload.features : [];
                        setSuggestions(features.map(toSuggestion).filter(function (candidate) { return Boolean(candidate.address); }));
                        return [3 /*break*/, 6];
                    case 4:
                        _error_1 = _a.sent();
                        setSuggestions([]);
                        return [3 /*break*/, 6];
                    case 5:
                        setIsFetchingSuggestions(false);
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        }); }, 250);
        return function () {
            window.clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [addressValue, isAddressInputFocusedByClick, open, selectedAddressQuery, token]);
    var applySuggestion = function (suggestion) {
        setValue("address", suggestion.address, { shouldValidate: true, shouldDirty: true });
        setValue("city", suggestion.city, { shouldValidate: true, shouldDirty: true });
        setValue("state", suggestion.state, { shouldValidate: true, shouldDirty: true });
        setValue("zip", suggestion.zip, { shouldValidate: true, shouldDirty: true });
        setSuggestions([]);
        setActiveSuggestionId(suggestion.id);
        setSelectedAddressQuery(suggestion.address);
    };
    return (React.createElement(RadixFlyOut, { open: open, onOpenChange: onOpenChange, title: title, description: description },
        React.createElement("form", { className: "address-flyout", onSubmit: handleSubmit(onSubmit) },
            React.createElement("div", { className: "address-flyout__content" },
                React.createElement("div", { className: "address-flyout__field" },
                    React.createElement(Controller, { name: "address", control: control, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            var normalizedAddress = String(field.value || "").trim();
                            var addressOptions = isAddressInputFocusedByClick
                                ? suggestions.map(function (suggestion) { return ({
                                    value: suggestion.id,
                                    label: suggestion.label,
                                }); })
                                : [];
                            var noResultsMessage = !isAddressInputFocusedByClick
                                ? ""
                                : !token
                                    ? "Address autocomplete is unavailable."
                                    : isFetchingSuggestions
                                        ? "Looking up address…"
                                        : normalizedAddress.length < 4
                                            ? "Type at least 4 characters to search."
                                            : "No matching addresses found.";
                            return (React.createElement(RadixComboBox, { label: "Address", id: "address-flyout-address", value: activeSuggestionId || "", inputValue: (_b = field.value) !== null && _b !== void 0 ? _b : "", options: addressOptions, onInputValueChange: function (nextAddressValue) {
                                    field.onChange(nextAddressValue);
                                    setActiveSuggestionId(null);
                                }, onValueChange: function (suggestionId) {
                                    var suggestion = suggestions.find(function (candidate) { return candidate.id === suggestionId; });
                                    if (!suggestion) {
                                        return;
                                    }
                                    applySuggestion(suggestion);
                                }, onInputMouseDown: function () {
                                    setIsAddressInputFocusedByClick(true);
                                }, onOpenChange: function (isOpen) {
                                    if (!isOpen) {
                                        setIsAddressInputFocusedByClick(false);
                                    }
                                }, onBlur: function () {
                                    field.onBlur();
                                    setIsAddressInputFocusedByClick(false);
                                }, ref: field.ref, ariaInvalid: !!errors.address, ariaDescribedBy: errors.address ? "address-flyout-address-error" : undefined, autoComplete: "street-address", noResultsMessage: noResultsMessage }));
                        } }),
                    errors.address ? React.createElement("span", { id: "address-flyout-address-error", className: "radix-error-message" }, errors.address.message) : null),
                React.createElement("div", { className: "address-flyout__field" },
                    React.createElement(Controller, { name: "address2", control: control, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "Address 2", id: "address-flyout-address2", value: (_b = field.value) !== null && _b !== void 0 ? _b : "", onChange: function (event) { return field.onChange(event.target.value); }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.address2, "aria-describedby": errors.address2 ? "address-flyout-address2-error" : undefined, autoComplete: "address-line2" }));
                        } }),
                    errors.address2 ? React.createElement("span", { id: "address-flyout-address2-error", className: "radix-error-message" }, errors.address2.message) : null),
                React.createElement("div", { className: "address-flyout__field" },
                    React.createElement(Controller, { name: "city", control: control, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "City", id: "address-flyout-city", value: (_b = field.value) !== null && _b !== void 0 ? _b : "", onChange: function (event) { return field.onChange(event.target.value); }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.city, "aria-describedby": errors.city ? "address-flyout-city-error" : undefined, autoComplete: "address-level2" }));
                        } }),
                    errors.city ? React.createElement("span", { id: "address-flyout-city-error", className: "radix-error-message" }, errors.city.message) : null),
                React.createElement("div", { className: "address-flyout__field" },
                    React.createElement(Controller, { name: "state", control: control, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (React.createElement(StateCodeComboBox, { label: "State", id: "address-flyout-state", value: (_b = field.value) !== null && _b !== void 0 ? _b : "", onChange: field.onChange, onBlur: field.onBlur, ref: field.ref, ariaInvalid: !!errors.state, ariaDescribedBy: errors.state ? "address-flyout-state-error" : undefined, autoComplete: "address-level1" }));
                        } }),
                    errors.state ? React.createElement("span", { id: "address-flyout-state-error", className: "radix-error-message" }, errors.state.message) : null),
                React.createElement("div", { className: "address-flyout__field" },
                    React.createElement(Controller, { name: "zip", control: control, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "Zip Code", id: "address-flyout-zip", value: (_b = field.value) !== null && _b !== void 0 ? _b : "", onChange: function (event) { return field.onChange(event.target.value); }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.zip, "aria-describedby": errors.zip ? "address-flyout-zip-error" : undefined, autoComplete: "postal-code" }));
                        } }),
                    errors.zip ? React.createElement("span", { id: "address-flyout-zip-error", className: "radix-error-message" }, errors.zip.message) : null)),
            React.createElement("div", { className: "user-account-modal-actions" },
                React.createElement(RadixButton, { className: "user-account-inline-button", type: "submit", disabled: !isValid || isSubmitting },
                    React.createElement(SaveIcon, null),
                    " ",
                    isSubmitting ? "Saving..." : submitLabel)))));
};
